import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { fetchKnownMappings, deleteKnownMappings, fetchFieldRequirements } from "@/services/api";
import { useUpload } from "@/hooks/useUpload";
import { useTelemetry } from "@/hooks/useTelemetry";
import { Button, AlertMessage, PageBanner, Spinner } from "@/components/reusable";
import type { KnownMapping, FieldRequirement } from "@/types";

export function KnownMappingsPage() {
  const { t } = useTranslation();
  const { savedMappings, saveMappings } = useUpload();
  const { track, trackError, startTimer } = useTelemetry();

  type SortField = "source" | "target" | "fieldId" | "provider";
  const [sort, setSort] = useState<{ field: SortField; direction: "asc" | "desc" }>({
    field: "fieldId",
    direction: "asc",
  });

  const [apiMappings, setApiMappings] = useState<KnownMapping[]>([]);
  const [fieldRequirements, setFieldRequirements] = useState<FieldRequirement[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  // Per-schema selected report type; absence or "All" means no report_type filter for that schema.
  const [selectedReportTypeBySchemaId, setSelectedReportTypeBySchemaId] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState<{
    variant: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    const stopTimer = startTimer();
    // Fetch known mappings and field requirements in parallel on mount.
    Promise.all([fetchKnownMappings(), fetchFieldRequirements()])
      .then(([mappings, requirements]) => {
        setApiMappings(mappings);
        setFieldRequirements(requirements);
        track("known_mappings_loaded", {
          mappingCount: mappings.length,
          fieldRequirementCount: requirements.length,
          durationMs: stopTimer(),
        });
      })
      .catch((err) => trackError(err, { context: "known_mappings_load" }))
      .finally(() => setLoading(false));
  }, [startTimer, track, trackError]);

  // Merge API mappings with user-saved mappings from the workflow
  const mappings = useMemo(() => {
    const merged = [...apiMappings];
    const existingKeys = new Set(
      apiMappings.map((m) => `${m.sourceField}::${m.targetField}::${m.schemaId}`)
    );
    for (const sm of savedMappings) {
      const key = `${sm.sourceField}::${sm.targetField}::${sm.schemaId}`;
      if (!existingKeys.has(key)) {
        merged.push(sm);
      }
    }
    return merged;
  }, [apiMappings, savedMappings]);

  const schemaDisplayNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of mappings) {
      if (!m.schemaId) continue;
      map.set(m.schemaId, m.schemaName || m.schemaId);
    }
    return map;
  }, [mappings]);

  const providers = [...schemaDisplayNameById.keys()].sort((a, b) =>
    (schemaDisplayNameById.get(a) ?? a).localeCompare(schemaDisplayNameById.get(b) ?? b)
  );

  /**
   * Nested lookup: schemaId → reportType → Set<fieldId>.
   * Built from klyra_field_requirements data fetched on mount.
   * Used to filter known mappings by (schema_id, field_id) when a report_type is chosen.
   */
  const fieldsBySchemaAndReportType = useMemo(() => {
    const outer = new Map<string, Map<string, Set<string>>>();
    for (const req of fieldRequirements) {
      if (!outer.has(req.schemaId)) outer.set(req.schemaId, new Map());
      const inner = outer.get(req.schemaId)!;
      if (!inner.has(req.reportType)) inner.set(req.reportType, new Set());
      inner.get(req.reportType)!.add(req.fieldId);
    }
    return outer;
  }, [fieldRequirements]);

  /**
   * Distinct, sorted report type options per schema — derived from fieldsBySchemaAndReportType.
   * Used to populate the per-schema report_type dropdown in the UI.
   */
  const reportTypesBySchemaId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [schemaId, reportTypeMap] of fieldsBySchemaAndReportType) {
      map.set(schemaId, [...reportTypeMap.keys()].sort());
    }
    return map;
  }, [fieldsBySchemaAndReportType]);

  // Stage 1: filter by selected provider pills (unchanged behaviour).
  const providerFiltered = selectedProviders.length
    ? mappings.filter((m) => selectedProviders.includes(m.schemaId))
    : mappings;

  /**
   * Mapping counts per (schemaId, reportType), used to show a count badge on each pill.
   * "All" count is the total number of mappings for that schema after the provider filter.
   */
  const mappingCountBySchemaAndReportType = useMemo(() => {
    const counts = new Map<string, Map<string, number>>();
    for (const m of providerFiltered) {
      if (!counts.has(m.schemaId)) counts.set(m.schemaId, new Map());
      const inner = counts.get(m.schemaId)!;
      // Always increment "All" for this schema.
      inner.set("All", (inner.get("All") ?? 0) + 1);
      // Increment each report_type this mapping's targetFieldId belongs to.
      const reportTypeMap = fieldsBySchemaAndReportType.get(m.schemaId);
      if (reportTypeMap) {
        for (const [rt, fieldIds] of reportTypeMap) {
          if (fieldIds.has(m.targetFieldId ?? "")) {
            inner.set(rt, (inner.get(rt) ?? 0) + 1);
          }
        }
      }
    }
    return counts;
  }, [providerFiltered, fieldsBySchemaAndReportType]);

  /**
   * Stage 2: filter by selected report_type per schema.
   * Only hides rows when a specific report_type (not "All") is selected for that schema.
   * The join key is (schemaId, targetFieldId) — schema-scoped to prevent cross-schema collisions.
   */
  const filtered = providerFiltered.filter((m) => {
    const selectedReportType = selectedReportTypeBySchemaId[m.schemaId] ?? "All";
    if (selectedReportType === "All") return true;
    const allowedFieldIds = fieldsBySchemaAndReportType.get(m.schemaId)?.get(selectedReportType);
    return allowedFieldIds ? allowedFieldIds.has(m.targetFieldId ?? "") : false;
  });

  const sorted = useMemo(() => {
    const getValue = (m: KnownMapping): string => {
      switch (sort.field) {
        case "source":
          return m.sourceField ?? "";
        case "target":
          return m.targetField ?? "";
        case "fieldId":
          return m.targetFieldId ?? "";
        case "provider":
          return m.schemaName ?? m.schemaId ?? "";
        default:
          return "";
      }
    };

    const dir = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      const cmp = av.localeCompare(bv, undefined, { sensitivity: "base", numeric: true });
      return cmp === 0 ? a.mappingId.localeCompare(b.mappingId) : cmp * dir;
    });
  }, [filtered, sort.direction, sort.field]);

  const allFilteredSelected =
    sorted.length > 0 && sorted.every((m) => selectedIds.has(m.mappingId));
  const someSelected = selectedIds.size > 0;

  const toggleProvider = useCallback(
    (p: string) => {
      setSelectedProviders((prev) => {
        const next = prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p];
        track("known_mappings_provider_filter_toggled", {
          provider: p,
          active: !prev.includes(p),
          activeProviderCount: next.length,
        });
        return next;
      });
      // Reset report_type selection when a provider is deselected so stale filters don't persist.
      setSelectedReportTypeBySchemaId((prev) => {
        if (!selectedProviders.includes(p)) return prev;
        const next = { ...prev };
        delete next[p];
        return next;
      });
    },
    [track, selectedProviders]
  );

  /**
   * Sets the active report_type filter for a specific schema.
   * Passing "All" (or omitting) clears the filter for that schema.
   */
  const selectReportType = useCallback(
    (schemaId: string, reportType: string) => {
      setSelectedReportTypeBySchemaId((prev) => {
        if (reportType === "All") {
          const next = { ...prev };
          delete next[schemaId];
          return next;
        }
        return { ...prev, [schemaId]: reportType };
      });
      track("known_mappings_report_type_filter_selected", { schemaId, reportType });
    },
    [track]
  );

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (sorted.every((m) => prev.has(m.mappingId))) {
        const next = new Set(prev);
        sorted.forEach((m) => next.delete(m.mappingId));
        return next;
      }
      const next = new Set(prev);
      sorted.forEach((m) => next.add(m.mappingId));
      return next;
    });
  }, [sorted]);

  const toggleSort = useCallback(
    (field: SortField) => {
      setSort((prev) => {
        if (prev.field !== field) return { field, direction: "asc" };
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      });
      track("known_mappings_sort_toggled", { field });
    },
    [track]
  );

  const sortIndicator = useCallback(
    (field: SortField) => {
      if (sort.field !== field) return "";
      return sort.direction === "asc" ? " ▲" : " ▼";
    },
    [sort.direction, sort.field]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await deleteKnownMappings(Array.from(selectedIds));
      setApiMappings((prev) => prev.filter((m) => !selectedIds.has(m.mappingId)));
      saveMappings(savedMappings.filter((m) => !selectedIds.has(m.mappingId)));
      track("known_mappings_deleted", { count: selectedIds.size });
      setSelectedIds(new Set());
      setDeleteAlert({
        variant: "success",
        message: `${selectedIds.size} mapping${selectedIds.size === 1 ? "" : "s"} deleted successfully.`,
      });
    } catch (err) {
      trackError(err as Error, { context: "known_mappings_delete" });
      setDeleteAlert({ variant: "error", message: "Failed to delete mappings. Please try again." });
    } finally {
      setDeleting(false);
      setTimeout(() => setDeleteAlert(null), 4000);
    }
  }, [selectedIds, savedMappings, saveMappings, track, trackError]);

  const handleSaveAll = useCallback(() => {
    track("known_mappings_save_all_clicked", {
      totalMappings: filtered.length,
      filteredByProviders: selectedProviders.length > 0,
    });
  }, [filtered.length, selectedProviders.length, track]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 animate-fade-in">
        <Spinner size="lg" />
        <p className="text-sm text-[var(--color-text-secondary)]">Loading known mappings...</p>
      </div>
    );
  }

  return (
    <>
      <PageBanner title={t("knownMappings.title")} subtitle={t("knownMappings.subtitle")} />

      {/* Provider filter */}
      <div className="mb-4 animate-fade-in-up stagger-2">
        <label className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 block">
          {t("knownMappings.selectProviders")}
        </label>
        <div className="mb-2 text-sm text-[var(--color-text-secondary)]">
          Click a schema pill to enable filtering by report type.
        </div>
        <div className="flex flex-wrap gap-2">
          {providers.map((p) => (
            <button
              key={p}
              onClick={() => toggleProvider(p)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-all duration-200 active:scale-[0.96] ${
                selectedProviders.includes(p)
                  ? "bg-[var(--color-button-primary-bg)] text-white border-[var(--color-button-primary-bg)] shadow-sm"
                  : "bg-white text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-brand-primary)] hover:shadow-sm"
              }`}
            >
              {schemaDisplayNameById.get(p) ?? p}
            </button>
          ))}
        </div>
      </div>

      {/* Per-schema report type filters — only shown when at least one provider is selected */}
      {selectedProviders.length > 0 && (
        <div className="mb-6 animate-fade-in-up stagger-3">
          <div className="flex flex-wrap gap-6">
            {selectedProviders.map((schemaId) => {
              const reportTypes = reportTypesBySchemaId.get(schemaId) ?? [];
              if (reportTypes.length === 0) return null;
              const activeReportType = selectedReportTypeBySchemaId[schemaId] ?? "All";
              return (
                <div key={schemaId}>
                  <label className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5 block">
                    {schemaDisplayNameById.get(schemaId) ?? schemaId}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {["All", ...reportTypes].map((rt) => {
                      const count = mappingCountBySchemaAndReportType.get(schemaId)?.get(rt) ?? 0;
                      return (
                        <button
                          key={rt}
                          onClick={() => selectReportType(schemaId, rt)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border transition-all duration-150 active:scale-[0.96] ${
                            activeReportType === rt
                              ? "bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)] border-[var(--color-brand-primary)] font-medium"
                              : "bg-white text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-brand-primary)]"
                          }`}
                        >
                          {rt}
                          <span
                            className={`inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full text-[10px] font-semibold px-1 ${
                              activeReportType === rt
                                ? "bg-[var(--color-brand-primary)] text-white"
                                : "bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]"
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      {sorted.length > 0 ? (
        <div className="rounded-xl border border-[var(--color-border-light)] overflow-hidden animate-fade-in-up stagger-3">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-secondary)]">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-[var(--color-border)] accent-[var(--color-button-primary-bg)] cursor-pointer"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("source")}
                    className="hover:text-[var(--color-text-primary)]"
                  >
                    {t("knownMappings.sourceColumn")}
                    {sortIndicator("source")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("target")}
                    className="hover:text-[var(--color-text-primary)]"
                  >
                    {t("knownMappings.targetColumn")}
                    {sortIndicator("target")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("fieldId")}
                    className="hover:text-[var(--color-text-primary)]"
                    title="field_id dictates the order of the schema shown in the outputs."
                  >
                    Field ID{sortIndicator("fieldId")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("provider")}
                    className="hover:text-[var(--color-text-primary)]"
                  >
                    {t("knownMappings.provider")}
                    {sortIndicator("provider")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m, i) => (
                <tr
                  key={m.mappingId}
                  onClick={() => toggleRow(m.mappingId)}
                  className={`border-t border-[var(--color-border-light)] transition-colors cursor-pointer ${
                    selectedIds.has(m.mappingId)
                      ? "bg-[var(--color-brand-primary)]/5"
                      : "hover:bg-[var(--color-bg-secondary)]"
                  }`}
                  style={{
                    animation: "fade-in 0.3s ease-out both",
                    animationDelay: `${i * 25}ms`,
                  }}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.mappingId)}
                      onChange={() => toggleRow(m.mappingId)}
                      className="rounded border-[var(--color-border)] accent-[var(--color-button-primary-bg)] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{m.sourceField}</td>
                  <td className="px-4 py-3">{m.targetField}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[var(--color-text-secondary)]">
                    {m.targetFieldId}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {m.schemaName || m.schemaId}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[var(--color-text-tertiary)] text-center py-8">{t("common.noData")}</p>
      )}

      {deleteAlert && (
        <div className="mt-4 animate-fade-in">
          <AlertMessage variant={deleteAlert.variant} message={deleteAlert.message} />
        </div>
      )}

      <div className="mt-4 flex justify-between animate-fade-in-up stagger-4">
        <div>
          {someSelected && (
            <Button onClick={handleDeleteSelected} disabled={deleting} variant="danger">
              {deleting
                ? t("common.deleting")
                : t("knownMappings.deleteSelected", { count: selectedIds.size })}
            </Button>
          )}
        </div>
        <Button onClick={handleSaveAll}>{t("knownMappings.saveAll")}</Button>
      </div>
    </>
  );
}
